/** Volunteer row for the management {@link DataTable}. */
import * as React from 'react';
import { User, BookOpen, CalendarDays, Timer, Target, LogOut, TrendingUp } from 'lucide-react';
import { Button } from '@/components/forms/Button';
import { LabelBadge } from '@/components/common/LabelBadge';
import { TableRow } from '@/components/common/data-table';
import { STAFF_STATUS_CONFIG } from '@/components/volunteers/volunteer-configs';
import {
  deriveVolunteerContact,
  resolveVolunteerPermission,
  formatVolunteerShortDate,
  computeVolunteerSeniority,
} from './volunteer-table-utils';
import type { Volunteer } from '@/types/volunteer.types';
import { normalizeStaffAccountStatus } from '@/types/user.types';
import { useRoleConfig } from '@/hooks/useRoleConfig';
import { toRoleColorCss } from '@/utils/role-label-utils';
import { formatDeactivationDate } from '@/utils/common-utils';
import type { InterfaceElementResponseDto } from '@/types/interface-elements.types';

/** Props for {@link VolunteerRow}. */
interface VolunteerRowProps {
  volunteer: Volunteer;
  interfaceElements: InterfaceElementResponseDto[];
  onView?: (id: string) => void;
  onEdit?: (id: string) => void;
  onDelete?: (id: string) => void;
  pageActions?: { canEditRow?: boolean; canDeleteRow?: boolean };
}

/** Volunteer row for the management {@link DataTable}. */
export const VolunteerRow: React.FC<VolunteerRowProps> = ({
  volunteer,
  interfaceElements,
  onView,
  onEdit,
  onDelete,
  pageActions,
}) => {
  const statusKey = normalizeStaffAccountStatus(String(volunteer.status));
  const statusConfig = STAFF_STATUS_CONFIG[statusKey] || STAFF_STATUS_CONFIG.active;
  const StatusIcon = statusConfig.icon;

  const profile = volunteer.userProfiles?.[0];
  const subjectNames = profile?.subjectNames || [];
  const subjectCount = Array.isArray(subjectNames) ? subjectNames.length : 0;
  const roleConfig = useRoleConfig();

  const canEdit = React.useMemo(
    () =>
      (pageActions?.canEditRow ?? true) &&
      resolveVolunteerPermission(volunteer.userRoles, interfaceElements, 'edit-role'),
    [pageActions?.canEditRow, volunteer.userRoles, interfaceElements],
  );

  const canDelete = React.useMemo(
    () =>
      (pageActions?.canDeleteRow ?? true) &&
      resolveVolunteerPermission(volunteer.userRoles, interfaceElements, 'delete-role'),
    [pageActions?.canDeleteRow, volunteer.userRoles, interfaceElements],
  );

  const engagementStartDate = profile?.engagementStartDate;
  const exitDate = profile?.exitDate;
  const targetHours = profile?.targetHours;
  const contact = deriveVolunteerContact(volunteer);

  return (
    <TableRow>
      <td>
        <div className="flex items-center gap-2">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-[color-mix(in_srgb,var(--color-1-nuance-1)_10%,transparent)]">
            <User className="w-4 h-4 text-[var(--color-1-nuance-1)]" />
          </div>
          <div className="flex flex-col">
            <span className="font-medium text-foreground">
              {volunteer.firstName} {volunteer.lastName}
            </span>
            {volunteer.displayName && (
              <span className="text-xs text-muted-foreground">{volunteer.displayName}</span>
            )}
          </div>
        </div>
      </td>

      <td>
        <div className="flex flex-col gap-0.5">
          <span className="flex items-center gap-2 text-sm text-foreground">
            <contact.icon
              className="w-4 h-4 shrink-0"
              style={{ color: 'var(--color-1-nuance-1)' }}
            />
            {contact.value}
          </span>
          {contact.secondaryValue && (
            <span className="text-xs text-muted-foreground">{contact.secondaryValue}</span>
          )}
        </div>
      </td>

      <td>
        <div className="flex flex-wrap gap-1">
          {volunteer.userRoles?.map((ur) => {
            const config = roleConfig[ur.role.code.toUpperCase()];
            const RoleIcon = config?.icon;
            const badgeColor = config?.colorClass ? toRoleColorCss(config.colorClass) : undefined;
            return (
              <LabelBadge
                key={ur.role.code}
                label={ur.role.label || config?.label || ur.role.code}
                icon={RoleIcon}
                variant="outlined"
                color={badgeColor}
                size="sm"
              />
            );
          })}
        </div>
      </td>

      <td>
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4" style={{ color: 'var(--color-1-nuance-1)' }} />
          <span className="text-sm text-foreground">
            {subjectCount} matière{subjectCount > 1 ? 's' : ''}
          </span>
        </div>
      </td>

      <td className="!px-3">
        <div className="flex items-center gap-1.5">
          <CalendarDays className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
          <span className="text-sm text-foreground whitespace-nowrap">
            {formatVolunteerShortDate(volunteer.createdAt)}
          </span>
        </div>
      </td>

      <td className="!px-3">
        <span className="text-sm text-foreground whitespace-nowrap">
          {computeVolunteerSeniority(engagementStartDate)}
        </span>
      </td>

      <td className="text-center !px-3">
        <div className="flex items-center gap-1 justify-center">
          <Timer className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
          <span className="text-sm text-foreground whitespace-nowrap">
            {volunteer.hoursDone ?? 0} h
          </span>
        </div>
      </td>

      <td className="text-center !px-3">
        <div className="flex items-center gap-1 justify-center">
          <Target className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
          <span className="text-sm text-foreground whitespace-nowrap">
            {targetHours != null ? `${targetHours} h` : '—'}
          </span>
        </div>
      </td>

      <td className="!px-3">
        <div className="flex items-center gap-1.5">
          <LogOut className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
          <span className="text-sm text-foreground whitespace-nowrap">
            {formatDeactivationDate(exitDate)}
          </span>
        </div>
      </td>

      <td className="text-center !px-3">
        <div className="flex items-center gap-1 justify-center">
          <TrendingUp className="w-3.5 h-3.5 text-[var(--color-1-nuance-1)] shrink-0" />
          <span className="text-sm text-foreground whitespace-nowrap">
            {volunteer.attendanceRate != null ? `${volunteer.attendanceRate}%` : '—'}
          </span>
        </div>
      </td>

      <td>
        <LabelBadge
          label={statusConfig.label}
          icon={StatusIcon}
          variant={statusConfig.badgeVariant}
          color={statusConfig.badgeColor}
          size="md"
        />
      </td>

      <td className="text-center">
        <div className="flex items-center justify-center gap-1">
          <Button preset="row-view" onClick={() => onView?.(volunteer.id)} />

          {canEdit && <Button preset="row-edit" onClick={() => onEdit?.(volunteer.id)} />}

          {canDelete && <Button preset="row-delete" onClick={() => onDelete?.(volunteer.id)} />}
        </div>
      </td>
    </TableRow>
  );
};
